<template>
  <div class="page teacher-leave-detail-page">
    <UiPageHeader title="请假申请详情" backTo="/teacher/leave" backText="返回请假管理" />

    <div v-if="detailLoading && !leave" class="loading-hint">加载中...</div>
    <div v-else-if="!leave || !leave.id" class="empty-hint">未找到该请假记录</div>
    <div v-else class="detail-layout">
      <UiCard class="module-card" style="--delay:0.05s">
        <h3 class="section-title">申请信息</h3>
        <div class="info-grid">
          <div class="info-item">
            <span class="info-label">申请人</span>
            <div class="info-value applicant">
              <div class="applicant-avatar" :style="{ background: leave.avatarBg }">
                <img
                  v-if="getStudentPhotoUrl(leave.photoFile)"
                  :src="getStudentPhotoUrl(leave.photoFile)"
                  :alt="leave.studentName || 'student avatar'"
                >
                <div v-else class="applicant-avatar__fallback" aria-hidden="true">
                  <svg viewBox="0 0 24 24" focusable="false">
                    <path
                      d="M12 12.75a4.875 4.875 0 1 0 0-9.75 4.875 4.875 0 0 0 0 9.75Zm0 2.25c-4.287 0-7.875 2.688-9 6.468A1.125 1.125 0 0 0 4.08 22.5h15.84a1.125 1.125 0 0 0 1.08-1.032C19.875 17.688 16.287 15 12 15Z"
                    />
                  </svg>
                </div>
              </div>
              <div>
                <strong>{{ leave.studentName }}</strong>
                <span class="info-sub">{{ leave.studentNo }} · {{ leave.className }}</span>
              </div>
            </div>
          </div>
          <div class="info-item">
            <span class="info-label">请假类型</span>
            <div class="info-value type-info-value">
              <UiBadge :tone="leave.type==='病假'?'warn':leave.type==='事假'?'info':'ok'">{{ leave.type }}</UiBadge>
            </div>
          </div>
          <div class="info-item">
            <span class="info-label">离校状态</span>
            <div class="info-value">
              <div class="left-status-detail" :class="leave.leftSchool ? 'left-yes' : 'left-no'">
                <span>{{ leave.leftSchool ? '已离校' : '未离校' }}</span>
                <span v-if="leave.leftSchool && leave.leftSchoolTime" class="left-time">{{ leave.leftSchoolTime }}</span>
              </div>
            </div>
          </div>
          <div class="info-item">
            <span class="info-label">请假时间</span>
            <div class="info-value">{{ leave.startTime }} ~ {{ leave.endTime }}（共{{ leave.days }}）</div>
          </div>
          <div class="info-item">
            <span class="info-label">请假事由</span>
            <div class="info-value reason-text">{{ leave.reason }}</div>
          </div>
          <div class="info-item">
            <span class="info-label">提交时间</span>
            <div class="info-value">{{ leave.applyTime }}</div>
          </div>
        </div>
      </UiCard>

      <UiCard v-if="approvalLoading" class="module-card" style="--delay:0.1s">
        <h3 class="section-title">审批信息</h3>
        <div class="section-loading">审批信息加载中...</div>
      </UiCard>

      <UiCard v-else-if="canApprove" class="module-card" style="--delay:0.1s">
        <h3 class="section-title">审批操作</h3>
        <div class="approval-actions">
          <button class="action-btn approve-btn" @click="showApproveModal = true">
            <span class="action-icon">✓</span>
            <span>通过</span>
          </button>
          <button class="action-btn reject-btn" @click="showRejectModal = true">
            <span class="action-icon">✕</span>
            <span>驳回</span>
          </button>
        </div>
        <div class="approval-hint">
          <p>通过后状态更新为"已通过"，同步至网页管理端及学生端、宿管、门卫</p>
        </div>
      </UiCard>

      <UiCard v-else-if="teacherApprovalStatus === 'approved' || teacherApprovalStatus === 'pending'" class="module-card" style="--delay:0.1s">
        <h3 class="section-title">审批进度</h3>
        <div class="result-box" :class="getResultStatusClass('待审批')">
          <div class="result-status">{{ teacherApprovalStatus === 'approved' ? '等待领导审批' : '待审批' }}</div>
          <div class="result-comment" v-if="lastApprovalComment">班主任审批意见：{{ lastApprovalComment }}</div>
        </div>
      </UiCard>

      <UiCard v-else class="module-card" style="--delay:0.1s">
        <h3 class="section-title">审批结果</h3>
        <div class="result-box" :class="getResultStatusClass(leave.status)">
          <div class="result-status">{{ leave.status }}</div>
          <div class="result-comment" v-if="leave.approvalComment">审批意见：{{ leave.approvalComment }}</div>
          <div class="result-time" v-if="leave.approvalTime">审批时间：{{ leave.approvalTime }}</div>
          <div class="result-person" v-if="leave.approvalPerson">审批人：{{ leave.approvalPerson }}</div>
        </div>
      </UiCard>

      <UiCard class="module-card sidebar-card" style="--delay:0.15s">
        <h3 class="section-title">审批流程</h3>
        <div v-if="approvalLoading" class="section-loading section-loading--compact">审批流程加载中...</div>
        <div v-if="leave.instanceDetail" class="flow-timeline">
          <div class="flow-step done">
            <div class="flow-dot"></div>
            <div class="flow-line"></div>
            <div class="flow-content">
              <div class="flow-title">提交申请</div>
              <div class="flow-person">{{ leave.submitterName || leave.createBy || leave.studentName }}</div>
              <div class="flow-time" v-if="leave.applyTime">{{ leave.applyTime }}</div>
            </div>
          </div>
          <div v-for="(rec, i) in leave.instanceDetail.approval_records" :key="i" class="flow-step" :class="rec.action === 'approve' ? 'done' : 'rejected'">
            <div class="flow-dot"></div>
            <div class="flow-line" v-if="i < leave.instanceDetail.approval_records.length - 1"></div>
            <div class="flow-content">
              <div class="flow-title">{{ rec.action === 'approve' ? '已通过' : '已驳回' }}</div>
              <div class="flow-person" v-if="rec.action === 'approve'">{{ rec.approverDisplayName || rec.approver_name || '—' }}{{ rec.comment ? '：' + rec.comment : '' }}</div>
              <div class="flow-person" v-else-if="rec.comment">{{ rec.comment }}</div>
              <div class="flow-time" v-if="rec.create_time">{{ formatInstanceTime(rec.create_time) }}</div>
            </div>
          </div>
          <div v-if="leave.instanceDetail.instance?.status === 'pending'" class="flow-step current">
            <div class="flow-dot"></div>
            <div class="flow-content">
              <div class="flow-title">待审批</div>
              <div class="flow-person">{{ leave.instanceDetail.approval_records?.length ? '等待下一级审批人操作' : '等待当前审批人操作' }}</div>
            </div>
          </div>
        </div>
        <div v-else class="flow-timeline">
          <div v-for="(step, i) in leave.approvalSteps" :key="i" class="flow-step" :class="step.status">
            <div class="flow-dot"></div>
            <div class="flow-line" v-if="i < leave.approvalSteps.length - 1"></div>
            <div class="flow-content">
              <div class="flow-title">{{ step.title }}</div>
              <div class="flow-person">{{ step.person }} · {{ step.role }}</div>
              <div class="flow-time" v-if="step.time">{{ step.time }}</div>
            </div>
          </div>
        </div>
      </UiCard>
    </div>

    <div v-if="showApproveModal" class="modal-overlay teacher-leave-detail-modal-overlay">
      <div class="modal-box">
        <h3>审批通过</h3>
        <label class="modal-field">
          <span>审批意见</span>
          <textarea v-model="approveComment" class="modal-textarea" rows="3" placeholder="可填写如: 同意请假, 返校后补交病历"></textarea>
        </label>
        <div class="modal-actions">
          <UiButton variant="primary" @click="doApprove">确认通过</UiButton>
          <UiButton variant="ghost" @click="showApproveModal=false">取消</UiButton>
        </div>
      </div>
    </div>

    <div v-if="showRejectModal" class="modal-overlay teacher-leave-detail-modal-overlay">
      <div class="modal-box">
        <h3>驳回申请</h3>
        <label class="modal-field">
          <span>驳回理由</span>
          <textarea v-model="rejectReason" class="modal-textarea" rows="3" placeholder="如: 请假事由未说明具体就诊医院, 需补充"></textarea>
        </label>
        <div class="modal-actions">
          <UiButton variant="danger" @click="doReject">确认驳回</UiButton>
          <UiButton variant="ghost" @click="showRejectModal=false">取消</UiButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import UiPageHeader from '../../components/ui/UiPageHeader.vue'
import UiCard from '../../components/ui/UiCard.vue'
import UiButton from '../../components/ui/UiButton.vue'
import UiBadge from '../../components/ui/UiBadge.vue'
import { getLeaveRecordDetail, parseLeaveStatus, approveLeave } from '@/api/leaveRecords'
import { getPendingApprovals, getApprovalInstance, getInstanceByLeaveId } from '@/api/approvalWorkflow'
import { getStudent, getStudents } from '@/api/students'
import { getImageUrl } from '@/api/upload'
import { formatLeaveDuration } from '@/utils/leaveDuration'
import { isPreviewableStudentPhoto } from '@/utils/studentPhoto'
import {
  buildStudentProfileLookup,
  extractStudentProfileList,
  findStudentProfile,
  resolveStudentPhotoFile
} from '@/utils/studentProfileLookup'
import { extractWorkflowMessage } from '@/utils/leaveWorkflow'

const AVATAR_BGS = ['linear-gradient(135deg,#667eea,#764ba2)', 'linear-gradient(135deg,#f093fb,#f5576c)', 'linear-gradient(135deg,#43e97b,#38f9d7)']
const TEACHER_LEAVE_DETAIL_SNAPSHOT_STORAGE_KEY = 'teacherLeaveDetailSnapshot'

function formatLeaveDate(str) {
  if (!str) return '—'
  const d = new Date(str)
  if (isNaN(d.getTime())) return str
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

function formatLeftSchoolTime(str) {
  if (!str) return ''
  const normalized = typeof str === 'string' ? str.replace(' ', 'T') : str
  const d = new Date(normalized)
  if (isNaN(d.getTime())) return str
  return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日${d.getHours()}时${d.getMinutes()}分`
}

function mapTeacherLeaveDetail(row, studentInfo = null, headTeacherName = null, approverDisplayName = null, explicitInstanceId = undefined) {
  const status = parseLeaveStatus(row)
  const createTime = formatLeaveDate(row.create_time)
  const updateTime = formatLeaveDate(row.update_time)
  const studentName = studentInfo?.name || row.student_name || row.student_id || '—'
  const submitterName = row.submitter_name || row.submitterName || row.create_by || studentName
  const step2Done = status === '已通过' || status === '已驳回'
  const teacherName = step2Done ? (approverDisplayName || headTeacherName || '—') : (headTeacherName || '—')
  const steps = [
    { title: '提交申请', person: submitterName, role: '发起人', status: 'done', time: createTime },
    { title: '班主任审批', person: teacherName, role: '班主任', status: step2Done ? 'done' : 'current', time: step2Done ? updateTime : '' }
  ]

  return {
    id: row.leave_id ?? row.id,
    instanceId: row.instance_id ?? row.instanceId ?? explicitInstanceId,
    currentApproverId: row.current_approver_id ?? row.currentApproverId ?? null,
    approvalStatus: row.approval_status ?? row.approvalStatus ?? null,
    studentName,
    createBy: row.create_by || '',
    submitterName,
    studentNo: studentInfo?.no || '',
    className: studentInfo?.className || row.class_name || '—',
    photoFile: studentInfo?.photoFile || resolveStudentPhotoFile(row) || '',
    avatarBg: AVATAR_BGS[0],
    type: row.leave_type || '其他',
    startTime: formatLeaveDate(row.start_time),
    endTime: formatLeaveDate(row.end_time),
    days: formatLeaveDuration(row.start_time, row.end_time),
    reason: row.reason || '—',
    destination: row.where_go || '—',
    applyTime: createTime,
    status,
    leftSchool: row.left_school ?? false,
    leftSchoolTime: formatLeftSchoolTime(row.left_school_time),
    approvalComment: row.approval_comment || '',
    approvalTime: updateTime,
    approvalPerson: approverDisplayName || '',
    attachments: row.attachments || [],
    approvalSteps: row.approval_steps || steps
  }
}

function normalizeStudentInfo(item = {}) {
  if (!item) return null
  return {
    name: item.student_name ?? item.name ?? '',
    no: item.student_number ?? item.studentNo ?? '',
    className: item.class_name ?? item.className ?? '',
    classId: item.class_id ?? item.classId ?? null,
    photoFile: resolveStudentPhotoFile(item)
  }
}

function mergeStudentInfo(base = null, extra = null) {
  if (!extra) return base
  if (!base) return { ...extra }
  return {
    name: extra.name || base.name || '',
    no: extra.no || base.no || '',
    className: extra.className || base.className || '',
    classId: extra.classId ?? base.classId ?? null,
    photoFile: extra.photoFile || base.photoFile || ''
  }
}

function normalizeLeaveId(value) {
  return String(value ?? '').trim()
}

function readTeacherLeaveDetailSnapshot(routeLeaveId) {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.sessionStorage?.getItem(TEACHER_LEAVE_DETAIL_SNAPSHOT_STORAGE_KEY)
    if (!raw) return null
    const snapshot = JSON.parse(raw)
    const snapshotId = normalizeLeaveId(snapshot?.leave_id ?? snapshot?.id)
    if (!snapshotId || snapshotId !== normalizeLeaveId(routeLeaveId)) return null
    return snapshot
  } catch {
    return null
  }
}

export default {
  name: 'TeacherLeaveDetailView',
  components: { UiPageHeader, UiCard, UiButton, UiBadge },
  data() {
    return {
      showApproveModal: false,
      showRejectModal: false,
      approveComment: '',
      rejectReason: '',
      detailLoading: false,
      approvalLoading: false,
      detailRequestId: 0,
      leave: null
    }
  },
  computed: {
    isHeadTeacher() { return this.$store.state.teacherIsHeadTeacher !== false },
    teacherApprovalStatus() {
      if (!this.leave) return ''
      const instanceStatus = String(this.leave.instanceDetail?.instance?.status ?? '').trim().toLowerCase()
      const approvalRecords = this.leave.instanceDetail?.approval_records ?? []
      if (instanceStatus === 'approved') return 'completed'
      if (instanceStatus === 'rejected') return 'rejected'
      if (instanceStatus === 'cancelled' || instanceStatus === 'canceled') return 'cancelled'
      if (instanceStatus === 'pending' && approvalRecords.length > 0) return 'approved'
      const approvalStatus = String(this.leave.approvalStatus ?? '').trim().toLowerCase()
      if (approvalStatus) return approvalStatus
      if (this.leave.status === '已驳回') return 'rejected'
      if (this.leave.status === '已通过') return 'completed'
      if (this.leave.status === '已销假') return 'cancelled'
      if (this.leave.status === '待审批') return 'pending'
      return ''
    },
    /** 当前用户是否为待审批环节的审批人（班主任已审批后，领导未审批时，班主任不应看到通过/拒绝按钮） */
    canApprove() {
      if (this.approvalLoading) return false
      return this.isHeadTeacher && this.teacherApprovalStatus === 'pending'
    },
    /** 最近一条审批意见（班主任已通过时，用于展示在「审批进度」卡片） */
    lastApprovalComment() {
      const recs = this.leave?.instanceDetail?.approval_records ?? []
      const last = recs.filter(r => r.action === 'approve').pop()
      return last?.comment || ''
    }
  },
  mounted() {
    this.$store.dispatch('classManage/fetchList', { page_size: 200 }).catch(() => {})
    this.applyCachedLeaveSnapshot()
    this.fetchLeaveDetail()
  },
  methods: {
    getResultStatusClass(status) {
      return {
        '待审批': 'pending',
        '已通过': 'ok',
        '已驳回': 'rejected',
        '已销假': 'cancelled'
      }[status] || 'pending'
    },
    getStudentPhotoUrl(path) {
      if (!isPreviewableStudentPhoto(path)) return ''
      return getImageUrl(path)
    },
    isCurrentDetailRequest(requestId) {
      return this.detailRequestId === requestId
    },
    applyCachedLeaveSnapshot() {
      const snapshot = readTeacherLeaveDetailSnapshot(this.$route.params.id)
      if (!snapshot) return false
      const instanceFromQuery = this.$route.query.instanceId ?? this.$route.query.instance_id
      const explicitInstanceId = instanceFromQuery != null
        ? Number(instanceFromQuery) || instanceFromQuery
        : undefined
      this.leave = mapTeacherLeaveDetail(snapshot, normalizeStudentInfo(snapshot), null, null, explicitInstanceId)
      this.detailLoading = false
      this.approvalLoading = true
      return true
    },
    async resolveStudentInfo(row) {
      let studentInfo = null
      const candidateIds = [row?.student_id, row?.student_user_id].filter(value => value != null && value !== '')

      for (const id of candidateIds) {
        try {
          const sRes = await getStudent(id)
          const s = sRes.data ?? sRes
          studentInfo = mergeStudentInfo(studentInfo, normalizeStudentInfo(s))
          if (studentInfo?.photoFile && (studentInfo?.classId != null || studentInfo?.className)) {
            return studentInfo
          }
        } catch {
          // Fall through to the student list lookup below.
        }
      }

      try {
        const listRes = await getStudents({ page_size: 1000 })
        const lookup = buildStudentProfileLookup(extractStudentProfileList(listRes))
        const profile = findStudentProfile(lookup, row, {
          id: row?.student_id,
          userId: row?.student_user_id,
          name: row?.student_name,
          studentNo: row?.student_number
        })
        return mergeStudentInfo(studentInfo, normalizeStudentInfo(profile))
      } catch {
        return studentInfo
      }
    },
    async fetchLeaveDetail() {
      const id = this.$route.params.id
      if (!id) return
      const requestId = ++this.detailRequestId
      const hadSnapshot = !!this.leave
      this.detailLoading = !hadSnapshot
      this.approvalLoading = hadSnapshot
      try {
        const res = await getLeaveRecordDetail(id)
        if (!this.isCurrentDetailRequest(requestId)) return
        const row = res.data ?? res
        if (!row) {
          if (!hadSnapshot) this.leave = null
          return
        }
        const instanceFromQuery = this.$route.query.instanceId ?? this.$route.query.instance_id
        this.leave = mapTeacherLeaveDetail(
          row,
          null,
          null,
          null,
          instanceFromQuery != null ? Number(instanceFromQuery) || instanceFromQuery : undefined
        )
        this.approvalLoading = true
        this.detailLoading = false
        this.hydrateLeaveMeta(row, requestId)
        this.loadApprovalData(requestId)
      } catch (e) {
        if (!hadSnapshot) this.leave = null
        if (hadSnapshot) this.approvalLoading = false
        alert(e.message || '获取请假详情失败')
      } finally {
        if (this.isCurrentDetailRequest(requestId) && !this.leave) {
          this.detailLoading = false
          this.approvalLoading = false
        }
      }
    },
    async hydrateLeaveMeta(row, requestId) {
      try {
        let studentInfo = await this.resolveStudentInfo(row)
        let headTeacherName = null
        if (studentInfo?.classId != null) {
          headTeacherName = await this.getHeadTeacherByClassId(studentInfo.classId)
        }
        if (!headTeacherName && studentInfo?.className) {
          headTeacherName = await this.getHeadTeacherByClassName(studentInfo.className)
        }
        const approverDisplayName = await this.getApproverDisplayName(row, headTeacherName)
        if (!this.isCurrentDetailRequest(requestId) || !this.leave) return
        const currentLeave = this.leave
        const enriched = mapTeacherLeaveDetail(row, studentInfo, headTeacherName, approverDisplayName, currentLeave.instanceId)
        this.leave = {
          ...currentLeave,
          studentName: enriched.studentName,
          createBy: enriched.createBy || currentLeave.createBy,
          submitterName: enriched.submitterName || currentLeave.submitterName,
          studentNo: enriched.studentNo,
          className: enriched.className,
          photoFile: enriched.photoFile || currentLeave.photoFile,
          approvalPerson: currentLeave.instanceDetail ? currentLeave.approvalPerson : enriched.approvalPerson,
          approvalSteps: currentLeave.instanceDetail ? currentLeave.approvalSteps : enriched.approvalSteps
        }
      } catch {
        // Keep the first-screen detail visible even if profile enrichment fails.
      }
    },
    async loadApprovalData(requestId) {
      try {
        if (!this.isCurrentDetailRequest(requestId) || !this.leave) return
        let instanceId = this.leave.instanceId
        if (instanceId == null && (this.leave.status === '待审批' || this.teacherApprovalStatus === 'approved')) {
          try {
            const pendingRes = await getPendingApprovals({ page: 1, page_size: 50 })
            const records = pendingRes?.data?.records ?? pendingRes?.records ?? []
            const leaveId = Number(this.leave.id) || this.leave.id
            const found = records.find(r => Number(r.leave_id ?? r.leaveId) === leaveId || String(r.leave_id ?? r.leaveId) === String(leaveId))
            instanceId = found?.instance_id ?? found?.instanceId ?? instanceId
          } catch { /* ignore */ }
        }
        if (instanceId == null && this.leave.id) {
          try {
            const instRes = await getInstanceByLeaveId(this.leave.id)
            const instData = instRes?.data ?? instRes
            instanceId = instData?.instance_id ?? instData?.instanceId ?? instanceId
          } catch { /* ignore */ }
        }
        if (!this.isCurrentDetailRequest(requestId) || !this.leave) return
        if (instanceId != null) {
          this.leave.instanceId = instanceId
          const instRes = await getApprovalInstance(instanceId)
          if (!this.isCurrentDetailRequest(requestId) || !this.leave) return
          const instData = instRes?.data ?? instRes
          await this.applyInstanceDetail(instData)
        }
      } catch {
        // Approval flow should not block the base detail from rendering.
      } finally {
        if (this.isCurrentDetailRequest(requestId)) {
          this.approvalLoading = false
        }
      }
    },
    async doApprove() {
      const comment = this.approveComment || '同意请假'
      if (!this.leave?.id) {
        alert('无法获取请假记录，请刷新后重试')
        return
      }
      try {
        const res = await approveLeave(this.leave.id, { approved: true, comment })
        this.showApproveModal = false
        this.approveComment = ''
        await this.fetchLeaveDetail()
        alert(extractWorkflowMessage(res, '审批已提交'))
      } catch (e) {
        alert(e.message || '操作失败')
      }
    },
    async doReject() {
      if (!this.rejectReason.trim()) { alert('请填写驳回理由'); return }
      if (!this.leave?.id) {
        alert('无法获取请假记录，请刷新后重试')
        return
      }
      try {
        const res = await approveLeave(this.leave.id, { approved: false, comment: this.rejectReason.trim() })
        this.showRejectModal = false
        this.rejectReason = ''
        await this.fetchLeaveDetail()
        alert(extractWorkflowMessage(res, '已驳回'))
      } catch (e) {
        alert(e.message || '操作失败')
      }
    },
    async applyInstanceDetail(payload) {
      if (!this.leave) return
      const instance = payload?.instance ?? payload ?? {}
      const rawRecords = payload?.approval_records ?? []
      const records = await this.resolveApprovalRecords(rawRecords)
      const orderedRecords = [...records].sort((a, b) => {
        const timeA = new Date(a?.create_time ?? '').getTime()
        const timeB = new Date(b?.create_time ?? '').getTime()
        if (!Number.isNaN(timeA) && !Number.isNaN(timeB) && timeA !== timeB) return timeA - timeB
        return Number(a?.record_id ?? 0) - Number(b?.record_id ?? 0)
      })
      const lastRecord = orderedRecords.length ? orderedRecords[orderedRecords.length - 1] : null
      const normalizedInstanceStatus = String(instance?.status ?? '').trim().toLowerCase()

      this.leave.instanceDetail = {
        instance,
        approval_records: orderedRecords
      }
      this.leave.approvalSteps = [
        {
          title: '提交申请',
          person: this.leave.submitterName || this.leave.createBy || this.leave.studentName,
          role: '发起人',
          status: 'done',
          time: this.leave.applyTime || ''
        },
        ...orderedRecords.map(rec => ({
          title: rec.action === 'approve' ? '已通过' : '已驳回',
          person: rec.approverDisplayName || rec.approver_name || '—',
          role: rec.node_name || '',
          status: rec.action === 'approve' ? 'done' : 'rejected',
          time: rec.create_time ? this.formatInstanceTime(rec.create_time) : ''
        })),
        ...(instance?.status === 'pending'
          ? [{
              title: instance.current_node_name || '待审批',
              person: instance.approver_name || '待审批人',
              role: instance.current_node_name || '',
              status: 'current',
              time: ''
            }]
          : [])
      ]
      this.leave.currentApproverId = instance.current_approver_id ?? this.leave.currentApproverId
      if (normalizedInstanceStatus) {
        this.leave.approvalStatus = normalizedInstanceStatus
        this.leave.status = parseLeaveStatus({ status: normalizedInstanceStatus })
      }
      if (lastRecord) {
        this.leave.approvalComment = lastRecord.comment || this.leave.approvalComment
        this.leave.approvalTime = lastRecord.create_time ? this.formatInstanceTime(lastRecord.create_time) : this.leave.approvalTime
        this.leave.approvalPerson = lastRecord.approverDisplayName || lastRecord.approver_name || this.leave.approvalPerson
      } else if (normalizedInstanceStatus && normalizedInstanceStatus !== 'pending') {
        this.leave.approvalTime = instance.update_time ? this.formatInstanceTime(instance.update_time) : this.leave.approvalTime
        this.leave.approvalPerson = instance.approver_name || this.leave.approvalPerson
      }
    },
    formatInstanceTime(str) {
      if (!str) return '—'
      const d = new Date(str)
      if (isNaN(d.getTime())) return str
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
    },
    /** 后端已在 approver_name 中返回审批人姓名，直接用于审批流程展示 */
    async resolveApprovalRecords(records) {
      if (!records?.length) return records
      return records.map(rec => ({ ...rec, approverDisplayName: rec.approver_name ?? rec.approverName ?? '—' }))
    },
    showDevTip() {
      this.$message?.({ type: 'info', message: '正在开发中' }) || alert('正在开发中')
    },
    /** 后端实例接口已返回审批人姓名，不再额外查询教师列表 */
    async getApproverDisplayName(row, headTeacherName) {
      const approverName = row.approver_name ?? row.approverName ?? row.approval_person ?? row.approvalPerson
      const updateBy = row.update_by ?? row.updateBy
      return approverName || headTeacherName || updateBy || ''
    },
    async getHeadTeacherByClassId(classId) {
      const list = this.$store.state.classManage?.list ?? []
      const cls = list.find(c => String(c.id ?? c.class_id) === String(classId))
      if (cls?.teacher || cls?.head_teacher) return cls.teacher || cls.head_teacher || cls.head_teacher_name || ''
      return ''
    },
    async getHeadTeacherByClassName(className) {
      const list = this.$store.state.classManage?.list ?? []
      const norm = (n) => {
        if (!n || typeof n !== 'string') return (n || '').trim()
        const m = (n || '').trim().match(/^([一二三])\((\d+)\)班$/)
        if (m) { const map = { 一: '高一', 二: '高二', 三: '高三' }; return (map[m[1]] || m[1]) + `(${m[2]})班` }
        return (n || '').trim()
      }
      const cls = list.find(c => norm(c.name ?? c.class_name) === norm(className))
      if (cls?.teacher || cls?.head_teacher) return cls.teacher || cls.head_teacher || cls.head_teacher_name || ''
      if (cls && (cls.id ?? cls.class_id) != null) return await this.getHeadTeacherByClassId(cls.id ?? cls.class_id)
      return ''
    }
  }
}
</script>

<style scoped>
.teacher-leave-detail-page {
  --teacher-leave-detail-blue: #003EFF;
  --teacher-leave-detail-blue-strong: #003CFF;
  --teacher-leave-detail-blue-deep: #1D66C6;
  --teacher-leave-detail-blue-soft: #CEE0FD;
  --teacher-leave-detail-panel: #F4F8FB;
  --teacher-leave-detail-line: rgba(0, 59, 255, 0.12);
  --teacher-leave-detail-shadow: 0 0 4.7px rgba(0, 0, 0, 0.15), 0 18px 36px rgba(0, 60, 255, 0.10);
  position: relative;
  background: url('~@/../public/bg.png') center top / cover no-repeat;
  margin: -24px -28px -40px;
  padding: 24px 28px 40px;
  min-height: calc(100vh - var(--topbar-height, 0px));
  min-height: calc(100dvh - var(--topbar-height, 0px));
  color: #0f172a;
}

.teacher-leave-detail-page :deep(.ui-card) {
  background: #fff;
  border-color: var(--teacher-leave-detail-line);
  box-shadow: var(--teacher-leave-detail-shadow);
}

.teacher-leave-detail-page :deep(.page-header__title),
.teacher-leave-detail-page .section-title,
.teacher-leave-detail-page .info-value,
.teacher-leave-detail-page .applicant strong,
.teacher-leave-detail-page .flow-title,
.teacher-leave-detail-page .modal-box h3 {
  color: #000;
}

.teacher-leave-detail-page :deep(.page-header__subtitle),
.teacher-leave-detail-page .loading-hint,
.teacher-leave-detail-page .empty-hint,
.teacher-leave-detail-page .section-loading,
.teacher-leave-detail-page .info-label,
.teacher-leave-detail-page .info-sub,
.teacher-leave-detail-page .left-time,
.teacher-leave-detail-page .approval-hint,
.teacher-leave-detail-page .flow-person,
.teacher-leave-detail-page .flow-time,
.teacher-leave-detail-page .modal-field span,
.teacher-leave-detail-page .result-comment,
.teacher-leave-detail-page .result-time,
.teacher-leave-detail-page .result-person {
  color: #5f6b7a;
}

.teacher-leave-detail-page .section-title,
.teacher-leave-detail-page .modal-textarea {
  border-color: var(--teacher-leave-detail-line);
}

.teacher-leave-detail-page .result-box,
.teacher-leave-detail-page .modal-textarea {
  background: rgba(206, 224, 253, 0.24);
}

.teacher-leave-detail-page .info-item {
  background: transparent;
}

.teacher-leave-detail-page .applicant-avatar__fallback {
  background: var(--teacher-leave-detail-panel);
  color: var(--teacher-leave-detail-blue-deep);
}

.teacher-leave-detail-page :deep(.ui-badge.info) {
  background: rgba(0, 59, 255, 0.10);
  color: #1D66C6;
}

.teacher-leave-detail-page :deep(.ui-badge.warn) {
  background: rgba(254, 241, 214, 0.96);
  color: #D97706;
}

.teacher-leave-detail-page :deep(.ui-badge.ok) {
  background: rgba(216, 245, 229, 0.96);
  color: #1B8F55;
}

.teacher-leave-detail-page :deep(.ui-badge.danger) {
  background: rgba(253, 233, 234, 0.96);
  color: #C5403E;
}

.teacher-leave-detail-page :deep(.ui-button.primary),
.teacher-leave-detail-modal-overlay :deep(.ui-button.primary) {
  background: #1B8F55;
  color: #fff;
  box-shadow: 0 5px 8.2px rgba(27, 143, 85, 0.26);
}

.teacher-leave-detail-page :deep(.ui-button.ghost),
.teacher-leave-detail-modal-overlay :deep(.ui-button.ghost) {
  background: rgba(0, 59, 255, 0.10);
  color: var(--teacher-leave-detail-blue-strong);
}

.teacher-leave-detail-page :deep(.ui-button.danger),
.teacher-leave-detail-modal-overlay :deep(.ui-button.danger) {
  background: #C5403E;
  color: #fff;
  box-shadow: 0 5px 8.2px rgba(197, 64, 62, 0.22);
}

.teacher-leave-detail-page .left-yes {
  color: #1B8F55;
}

.teacher-leave-detail-page .left-no {
  color: #D97706;
}

.teacher-leave-detail-page .approve-btn {
  background: linear-gradient(135deg, #1B8F55, #0F7A45);
  color: #fff;
  box-shadow: 0 5px 8.2px rgba(27, 143, 85, 0.26);
}

.teacher-leave-detail-page .approve-btn:hover {
  box-shadow: 0 8px 20px rgba(27, 143, 85, 0.30);
}

.teacher-leave-detail-page .reject-btn {
  background: linear-gradient(135deg, #C5403E, #9F2F2D);
  color: #fff;
  box-shadow: 0 5px 8.2px rgba(197, 64, 62, 0.22);
}

.teacher-leave-detail-page .reject-btn:hover {
  box-shadow: 0 8px 20px rgba(197, 64, 62, 0.28);
}

.teacher-leave-detail-page .result-box.pending {
  background: rgba(254, 241, 214, 0.96);
  border-color: rgba(245, 158, 11, 0.28);
}

.teacher-leave-detail-page .result-box.ok {
  background: rgba(216, 245, 229, 0.96);
  border-color: rgba(27, 143, 85, 0.26);
}

.teacher-leave-detail-page .result-box.rejected {
  background: rgba(253, 233, 234, 0.96);
  border-color: rgba(197, 64, 62, 0.22);
}

.teacher-leave-detail-page .result-box.cancelled {
  background: rgba(241, 245, 249, 0.96);
  border-color: rgba(100, 116, 139, 0.18);
}

.teacher-leave-detail-page .result-box.pending .result-status {
  color: #D97706;
}

.teacher-leave-detail-page .result-box.ok .result-status {
  color: #1B8F55;
}

.teacher-leave-detail-page .result-box.rejected .result-status {
  color: #C5403E;
}

.teacher-leave-detail-page .result-box.cancelled .result-status {
  color: #64748b;
}

.teacher-leave-detail-page .flow-dot {
  border-color: rgba(0, 59, 255, 0.16);
  background: #fff;
}

.teacher-leave-detail-page .flow-line {
  background: rgba(0, 59, 255, 0.12);
}

.teacher-leave-detail-page .flow-step.done .flow-dot {
  border-color: #1B8F55;
  background: #1B8F55;
}

.teacher-leave-detail-page .flow-step.current .flow-dot {
  border-color: #F59E0B;
  background: #F59E0B;
}

.teacher-leave-detail-page .flow-step.rejected .flow-dot {
  border-color: #C5403E;
  background: #C5403E;
}

.teacher-leave-detail-page .flow-step.done .flow-line {
  background: #1B8F55;
}

.teacher-leave-detail-page .modal-textarea {
  color: #0f172a;
  box-shadow: 0 0 4.7px rgba(0, 0, 0, 0.10);
}

.teacher-leave-detail-page .modal-textarea:focus {
  border-color: var(--teacher-leave-detail-blue);
  box-shadow: 0 0 0 3px rgba(0, 59, 255, 0.12), 0 0 4.7px rgba(0, 0, 0, 0.10);
  outline: none;
}

.teacher-leave-detail-modal-overlay {
  background: rgba(15, 23, 42, 0.38);
}

.teacher-leave-detail-modal-overlay .modal-box {
  background: #EAF2FF !important;
  border: 1px solid rgba(0, 59, 255, 0.12);
  box-shadow: 0 0 4.7px rgba(0, 0, 0, 0.15), 0 18px 36px rgba(0, 60, 255, 0.10);
}

.teacher-leave-detail-modal-overlay .modal-textarea {
  background: #fff;
  border-color: rgba(0, 59, 255, 0.18);
}

.loading-hint, .empty-hint { text-align: center; padding: 48px; color: var(--muted); font-size: 14px; }
.section-loading { text-align: center; padding: 48px 16px; color: var(--muted); font-size: 14px; }
.section-loading--compact { padding-top: 0; padding-bottom: 16px; }
.detail-layout { display: grid; grid-template-columns: 1fr 280px; gap: 20px; }
.detail-layout > .module-card:nth-child(1) { grid-column: 1; }
.detail-layout > .module-card:nth-child(2) { grid-column: 1; }
.sidebar-card { grid-column: 2; grid-row: 1 / 3; }

.section-title { font-size: 16px; font-weight: 700; margin-bottom: 18px; padding-bottom: 12px; border-bottom: 1px solid rgba(0,0,0,0.06); }

.info-grid { display: flex; flex-direction: column; gap: 16px; }
.info-item { display: flex; gap: 12px; }
.info-label { min-width: 80px; font-size: 13px; color: var(--muted); padding-top: 2px; flex-shrink: 0; }
.info-value { font-size: 14px; flex: 1; }
.type-info-value { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.info-sub { font-size: 12px; color: var(--muted); margin-left: 6px; }
.reason-text { line-height: 1.7; }
.left-status-detail {
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  font-size: 14px;
  font-weight: 700;
  margin-top: 0;
}
.left-yes { color: #28c76f; }
.left-no { color: #f0b429; }
.left-time { font-size: 13px; font-weight: 500; color: var(--muted); margin-left: 0; white-space: nowrap; line-height: 1.5; }

.applicant { display: flex; gap: 10px; align-items: center; }
.applicant-avatar {
  width: 40px; height: 40px; border-radius: 12px;
  color: white; font-weight: 700; font-size: 16px;
  overflow: hidden;
  display: grid; place-items: center; flex-shrink: 0;
}
.applicant-avatar img,
.applicant-avatar__fallback {
  width: 100%;
  height: 100%;
}
.applicant-avatar img {
  display: block;
  object-fit: cover;
}
.applicant-avatar__fallback {
  display: grid;
  place-items: center;
  background: #eef2f7;
  color: #94a3b8;
}
.applicant-avatar__fallback svg {
  width: 20px;
  height: 20px;
  fill: currentColor;
}

.att-item { display: flex; gap: 8px; align-items: center; margin-bottom: 6px; }
.att-icon { font-size: 16px; }
.att-name { font-weight: 600; font-size: 13px; }
.att-size { font-size: 11px; color: var(--muted); }
.att-btn {
  border: none; border-radius: 6px; padding: 3px 10px;
  background: rgba(21,122,110,0.1); color: var(--brand);
  font-size: 11px; font-weight: 600; cursor: pointer;
}
.att-btn:hover { background: rgba(21,122,110,0.18); }
.no-att { color: var(--muted); font-size: 13px; }

.approval-actions { display: flex; gap: 14px; margin-bottom: 14px; }
.action-btn {
  display: flex; align-items: center; gap: 8px;
  padding: 14px 32px; border: none; border-radius: 12px;
  font-size: 15px; font-weight: 700; cursor: pointer;
  transition: transform 0.15s, box-shadow 0.15s;
}
.approve-btn { background: linear-gradient(135deg, #28c76f, #1e8449); color: white; }
.approve-btn:hover { transform: translateY(-2px); box-shadow: 0 8px 20px rgba(40,199,111,0.35); }
.reject-btn { background: linear-gradient(135deg, #ea5455, #c44545); color: white; }
.reject-btn:hover { transform: translateY(-2px); box-shadow: 0 8px 20px rgba(234,84,85,0.35); }
.action-icon { font-size: 18px; font-weight: 800; }
.approval-hint { font-size: 12px; color: var(--muted); line-height: 1.6; }

.result-box { padding: 18px; border-radius: 12px; display: flex; flex-direction: column; gap: 6px; }
.result-box.pending { background: rgba(240,180,41,0.1); border: 1px solid rgba(240,180,41,0.24); }
.result-box.ok { background: rgba(40,199,111,0.08); border: 1px solid rgba(40,199,111,0.2); }
.result-box.rejected { background: rgba(234,84,85,0.06); border: 1px solid rgba(234,84,85,0.15); }
.result-box.cancelled { background: rgba(100,116,139,0.08); border: 1px solid rgba(100,116,139,0.18); }
.result-status { font-size: 18px; font-weight: 800; }
.result-box.pending .result-status { color: #b7791f; }
.result-box.ok .result-status { color: #28c76f; }
.result-box.rejected .result-status { color: #ea5455; }
.result-box.cancelled .result-status { color: #64748b; }
.result-comment, .result-time, .result-person { font-size: 13px; color: #555; }

.flow-timeline { position: relative; padding-left: 24px; margin-bottom: 16px; }
.flow-step { position: relative; padding-bottom: 20px; }
.flow-step:last-child { padding-bottom: 0; }
.flow-dot {
  position: absolute; left: -24px; top: 2px;
  width: 14px; height: 14px; border-radius: 50%;
  border: 3px solid #ddd; background: white;
}
.flow-line { position: absolute; left: -18px; top: 18px; bottom: 0; width: 2px; background: #eee; }
.flow-step.done .flow-dot { border-color: #28c76f; background: #28c76f; }
.flow-step.current .flow-dot { border-color: #f0b429; background: #f0b429; animation: pulse 1.5s infinite; }
.flow-step.rejected .flow-dot { border-color: #ea5455; background: #ea5455; }
.flow-step.done .flow-line { background: #28c76f; }
.flow-title { font-weight: 700; font-size: 13px; }
.flow-person { font-size: 12px; color: var(--muted); margin-top: 2px; }
.flow-time { font-size: 11px; color: var(--muted); margin-top: 2px; }

.modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.45); display: grid; place-items: center; z-index: 1000; }
.modal-box {
  background: white; border-radius: 16px; padding: 28px;
  width: 90%; max-width: 440px; display: flex; flex-direction: column; gap: 16px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.25);
}
.modal-box h3 { font-size: 18px; font-weight: 700; }
.modal-field { display: flex; flex-direction: column; gap: 6px; }
.modal-field span { font-size: 13px; color: var(--muted); }
.modal-textarea {
  width: 100%; border: 1px solid var(--line); border-radius: 10px;
  padding: 12px; font-size: 13px; font-family: inherit; resize: vertical;
}
.modal-actions { display: flex; gap: 10px; }

@keyframes pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.5; } }

@media (max-width: 1200px) {
  .teacher-leave-detail-page {
    margin: -20px -20px -32px;
    padding: 20px 20px 32px;
  }
}

@media (max-width: 1024px) {
  .teacher-leave-detail-page {
    margin: -18px -16px -28px;
    padding: 18px 16px 28px;
  }

  .detail-layout { grid-template-columns: 1fr; }
  .sidebar-card { grid-column: 1; grid-row: auto; }
}

@media (max-width: 768px) {
  .teacher-leave-detail-page {
    background: url('~@/../public/bg.png') center top / 100% 100% no-repeat;
    margin: -12px -12px -24px;
    padding: 12px 12px 24px;
    min-height: 100svh;
    min-height: 100dvh;
  }
}

@media (max-width: 480px) {
  .teacher-leave-detail-page {
    margin: -8px -10px -20px;
    padding: 8px 10px 20px;
  }

  .info-label { min-width: auto; }
  .modal-box { max-width: 95vw; padding: 16px; }
}
</style>
